import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatMoney } from '@/utils/money';
import type { CustomerProduct } from '@restaurant/api-contracts';
import type { SubmitRoundBody } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  Preloader,
  Searchbar,
  Segmented,
  Stepper,
  Subnavbar,
  f7,
} from 'framework7-react';
import { useEffect, useMemo, useState } from 'react';
import { type DraftLine, lineUnit } from '../lib/roundDraft';
import { useRoundDraft } from '../stores/roundDraft.store';
import {
  useAddSeat,
  useDineInMenu,
  useStaffSubmitRound,
  useTableSession,
} from '../hooks/useTableOrdering';

type Tab = 'menu' | 'order';

/**
 * Add-a-round - a pushed page split by a segmented control into "Menu" and
 * "Order". The order is always one tap away via the segment (and its live
 * count), so a long menu never buries the cart at the bottom of a scroll.
 * Dishes with options push a configure page; the shared `useRoundDraft` store
 * carries the in-progress round across that push.
 */
export default function AddRoundPage({ f7route }: { f7route: { params: { sessionId: string } } }) {
  const tAll = useT();
  const t = tAll.tableOrdering.addRound;
  const sessionId = f7route.params.sessionId;
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: menu, isLoading } = useDineInMenu();
  const { data: session } = useTableSession(sessionId);
  const submitRound = useStaffSubmitRound();
  const addSeat = useAddSeat();

  const lines = useRoundDraft((s) => s.lines);
  const note = useRoundDraft((s) => s.note);
  const addLine = useRoundDraft((s) => s.addLine);
  const setQty = useRoundDraft((s) => s.setQty);
  const setSeat = useRoundDraft((s) => s.setSeat);
  const setNote = useRoundDraft((s) => s.setNote);

  const seats = session?.seats ?? [];
  const seatLabelFor = (seatId: string | null): string => {
    if (seatId === null) return t.shared;
    const s = seats.find((x) => x.id === seatId);
    return s ? (s.label ?? t.seatOrdinal(s.ordinal)) : t.shared;
  };

  function addNewSeatFor(line: DraftLine) {
    addSeat.mutate(
      { sessionId, body: {} },
      {
        onSuccess: (s) => {
          const newest = [...s.seats].sort((a, b) => b.ordinal - a.ordinal)[0];
          if (newest) setSeat(line.key, newest.id);
          f7.toast.show({ text: t.seatAdded, closeTimeout: 1600, position: 'bottom' });
        },
      },
    );
  }

  function openSeatPicker(line: DraftLine) {
    const tick = (on: boolean) => (on ? '  ✓' : '');
    f7.actions
      .create({
        buttons: [
          [
            { text: t.assignTo, label: true },
            ...seats.map((s) => ({
              text: `${s.label ?? t.seatOrdinal(s.ordinal)}${tick(line.seatId === s.id)}`,
              onClick: () => setSeat(line.key, s.id),
            })),
            {
              text: `${t.shared}${tick(line.seatId === null)}`,
              onClick: () => setSeat(line.key, null),
            },
            { text: t.addSeat, strong: true, onClick: () => addNewSeatFor(line) },
          ],
          [{ text: tAll.cancel, color: 'red' }],
        ],
      })
      .open();
  }

  const [tab, setTab] = useState<Tab>('menu');
  const [query, setQuery] = useState('');

  // Start a fresh round for this tab. Guarded so returning from the configure
  // page (which keeps this page mounted) never wipes the draft.
  useEffect(() => {
    const s = useRoundDraft.getState();
    if (s.sessionId !== sessionId) s.reset(sessionId);
  }, [sessionId]);

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 1600, position: 'bottom' });

  const filtered = useMemo(() => {
    if (!menu) return [];
    const q = query.trim().toLowerCase();
    return menu.categories
      .map((c) => ({
        ...c,
        products: c.products.filter((p) => p.available && (!q || p.name.toLowerCase().includes(q))),
      }))
      .filter((c) => c.products.length > 0);
  }, [menu, query]);

  function configure(productId: string, editKey?: string) {
    const edit = editKey ? `&edit=${encodeURIComponent(editKey)}` : '';
    f7.views.current.router.navigate(
      `/tables/session/${sessionId}/add-round/configure?product=${productId}${edit}`,
    );
  }

  function pickProduct(p: CustomerProduct) {
    if (p.options.length === 0) {
      addLine(p, [], null);
      toast(t.addedItem(p.name));
      return;
    }
    configure(p.id);
  }

  function send() {
    if (lines.length === 0) {
      toast(t.tapDishes);
      setTab('menu');
      return;
    }
    const body: SubmitRoundBody = {
      items: lines.map((l) => ({
        productId: l.product.id,
        quantity: l.quantity,
        seatId: l.seatId,
        choices: l.choices.map((c) => ({
          optionId: c.optionId,
          optionChoiceId: c.optionChoiceId,
        })),
      })),
      ...(note.trim() ? { guestNote: note.trim() } : {}),
    };
    submitRound.mutate(
      { sessionId, body },
      {
        onSuccess: () => {
          toast(t.roundSentKitchen);
          f7.views.current.router.back();
        },
        onError: (e: unknown) => toast(e instanceof Error ? e.message : t.failedSend),
      },
    );
  }

  const total = lines.reduce((s, l) => s + lineUnit(l) * l.quantity, 0);
  const count = lines.reduce((s, l) => s + l.quantity, 0);

  return (
    <Page name="add-round" onPageBeforeRemove={() => useRoundDraft.getState().clear()}>
      <Navbar title={t.title} backLink={tAll.cancel}>
        <NavRight>
          <Link onClick={send}>{t.send}</Link>
        </NavRight>
        <Subnavbar>
          <Segmented strong round>
            <Button active={tab === 'menu'} onClick={() => setTab('menu')} round>
              {t.menu}
            </Button>
            <Button active={tab === 'order'} onClick={() => setTab('order')} round>
              {t.orderTab(count)}
            </Button>
          </Segmented>
        </Subnavbar>
      </Navbar>

      {tab === 'menu' ? (
        <>
          <Searchbar
            disableButton
            backdrop={false}
            placeholder={t.searchMenu}
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          />
          {isLoading ? (
            <Block className="text-align-center" style={{ paddingTop: 40 }}>
              <Preloader />
            </Block>
          ) : !menu ? (
            <Block className="text-align-center text-color-gray" style={{ paddingTop: 40 }}>
              {t.noMenu}
            </Block>
          ) : (
            filtered.map((cat) => (
              <div key={cat.id}>
                <BlockTitle>{cat.name}</BlockTitle>
                <List dividers strongIos insetIos>
                  {cat.products.map((p) => {
                    const configurable = p.options.length > 0;
                    return (
                      <ListItem
                        key={p.id}
                        link="#"
                        chevronCenter={configurable}
                        noChevron={!configurable}
                        title={p.name}
                        footer={configurable ? t.chooseOptions : undefined}
                        after={formatMoney(p.price, currency)}
                        onClick={() => pickProduct(p)}
                      />
                    );
                  })}
                </List>
              </div>
            ))
          )}
        </>
      ) : lines.length === 0 ? (
        <Block className="text-align-center text-color-gray" style={{ paddingTop: 48 }}>
          {t.noItems}
        </Block>
      ) : (
        <>
          <List dividers strongIos insetIos>
            {lines.map((l) => {
              const hasOptions = l.choices.length > 0;
              return (
                <ListItem
                  key={l.key}
                  title={l.product.name}
                  footer={hasOptions ? l.choices.map((c) => c.label).join(', ') : undefined}
                >
                  <div slot="after" className="to-pad-line">
                    <div className="to-pad-line__top">
                      {hasOptions && (
                        <Link
                          className="to-pad-line__edit"
                          onClick={() => configure(l.product.id, l.key)}
                        >
                          {t.edit}
                        </Link>
                      )}
                      <span className="to-pad-line__price">
                        {formatMoney(lineUnit(l) * l.quantity, currency)}
                      </span>
                    </div>
                    <Stepper
                      small
                      round
                      value={l.quantity}
                      min={0}
                      max={99}
                      onStepperChange={(v: number) => setQty(l.key, v)}
                    />
                    <button
                      type="button"
                      className={`to-pad-line__seat${l.seatId ? ' to-pad-line__seat--set' : ''}`}
                      onClick={() => openSeatPicker(l)}
                    >
                      <i className="f7-icons">person_crop_circle</i>
                      <span>{seatLabelFor(l.seatId)}</span>
                    </button>
                  </div>
                </ListItem>
              );
            })}
          </List>
          <List dividers strongIos insetIos>
            <ListInput
              type="text"
              placeholder={t.noteName}
              maxlength={500}
              value={note}
              onInput={(e) => setNote((e.target as HTMLInputElement).value)}
            />
          </List>
          <Block>
            <div className="to-pad-summary">
              {t.summaryLine(count, formatMoney(total, currency))}
            </div>
          </Block>
        </>
      )}
      <div style={{ height: 'calc(16px + var(--f7-safe-area-bottom, 0px))' }} />
    </Page>
  );
}
